// src/scenes/CreditsScene.js
// ─────────────────────────────────────────────────────────────────────────────
// End-game credits screen (shown when the player completes all levels).
//   · Full artwork background (background_final_credits.png)
//   · Pazurumon logo fixed at the top
//   · Cinematic auto-scroll credits roll
//   · FECHAR button exits to TitleScene at any time
//
// 🔊 MUSIC: search for "MUSIC HOOK" comments — one line to uncomment per step
//
// ── HOW TO CONNECT ────────────────────────────────────────────────────────────
// In LevelScene.js, when the player clears the last level, call:
//   this.scene.start("CreditsScene");
// ─────────────────────────────────────────────────────────────────────────────

import Phaser          from "phaser";
import logoImg         from "../assets/logo/pazuromonlogojap1.png";
import creditsBg       from "../assets/backgrounds/background_final_credits.png";
import creditsMusicUrl from "../assets/audio/music/creditscreen_music.ogg";
import { I18n }        from "../game/I18n.js";

const GOLD      = 0xfbbf24;
const GOLD_LITE = 0xfde68a;

// ── CREDITS CONTENT ───────────────────────────────────────────────────────────
// Types: section | name | role | subtitle | body | link | closing | highlight |
//        divider | spacer
const CREDITS = [
  { type: "spacer",    h: 40 },
  { type: "section",   label: "PAZZURUMON CHALLENGE GAME" },
  { type: "spacer",    h: 8  },
  { type: "subtitle",  label: "is a PLICAGAMES game" },
  { type: "spacer",    h: 56 },

  { type: "body",      label: "Created by" },
  { type: "spacer",    h: 8  },
  { type: "name",      label: "Miguel Dufresne" },
  { type: "role",      label: "CEO PLICAGAMES" },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "DESIGN / DIRECTION" },
  { type: "spacer",    h: 16 },
  { type: "name",      label: "Miguel Dufresne" },
  { type: "spacer",    h: 40 },

  { type: "section",   label: "CODE" },
  { type: "spacer",    h: 16 },
  { type: "name",      label: "Claude Red" },
  { type: "spacer",    h: 40 },

  { type: "section",   label: "NARRATIVE & WORLDBUILDING" },
  { type: "spacer",    h: 16 },
  { type: "name",      label: "Miguel Dufresne" },
  { type: "name",      label: "Claude Red" },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "ART ASSETS" },
  { type: "spacer",    h: 16 },
  { type: "body",      label: "Character and sprite assets provided by" },
  { type: "spacer",    h: 8  },
  { type: "name",      label: "Aaron Sprites" },
  { type: "link",      label: "aaronsprites.itch.io" },
  { type: "spacer",    h: 6  },
  { type: "role",      label: "Used under respective license terms." },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "NOXYS ANIMATOR" },
  { type: "spacer",    h: 16 },
  { type: "name",      label: "Gerson Soma" },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "MUSIC" },
  { type: "spacer",    h: 16 },
  { type: "body",      label: "Generated using SUNO AI platform" },
  { type: "link",      label: "suno.ai" },
  { type: "spacer",    h: 6  },
  { type: "role",      label: "Used in accordance with platform terms and licensing." },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "SOUND EFFECTS" },
  { type: "spacer",    h: 16 },
  { type: "name",      label: "Mixkit  (Envato)" },
  { type: "link",      label: "mixkit.co/free-sound-effects/game" },
  { type: "spacer",    h: 6  },
  { type: "role",      label: "Used under Mixkit License." },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "GAME ENGINE" },
  { type: "spacer",    h: 16 },
  { type: "name",      label: "Phaser.js" },
  { type: "link",      label: "phaser.io" },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "QA TESTING" },
  { type: "spacer",    h: 16 },
  { type: "name",      label: "Rafael" },
  { type: "name",      label: "Gabriel" },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "COMMUNITY ORGANIZERS" },
  { type: "spacer",    h: 16 },
  { type: "name",      label: "Artur" },
  { type: "name",      label: "Rafael" },
  { type: "name",      label: "Gabriel" },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "SPECIAL THANKS" },
  { type: "spacer",    h: 16 },
  { type: "body",      label: "Early supporters and the Pazzurumon community" },
  { type: "spacer",    h: 64 },

  { type: "divider"        },
  { type: "spacer",    h: 36 },

  { type: "section",   label: "LEGAL NOTICE" },
  { type: "spacer",    h: 16 },
  { type: "body",      label: "PAZZURUMON is an original game and intellectual property." },
  { type: "spacer",    h: 10 },
  { type: "body",      label: "All trademarks, names, characters, and content are either original, properly licensed, or used under applicable terms." },
  { type: "spacer",    h: 10 },
  { type: "body",      label: "No affiliation with Nintendo, Pokémon, or any related entities is implied." },
  { type: "spacer",    h: 56 },

  { type: "divider"        },
  { type: "spacer",    h: 36 },

  { type: "closing",   label: "© 2026 Plica Games" },
  { type: "closing",   label: "All Rights Reserved" },
  { type: "spacer",    h: 8  },
  { type: "role",      label: "Pazzurumon™ is a Plica Games brand." },
  { type: "spacer",    h: 56 },

  { type: "section",   label: "FIND US" },
  { type: "spacer",    h: 16 },
  { type: "body",      label: "Wishlist on Steam  •  Follow the Project  •  Become a Founder" },
  { type: "spacer",    h: 16 },
  { type: "body",      label: "MERCH STORE OFICIAL" },
  { type: "link",      label: "WWW.PAZZURUMON-OFFICIAL.MYSHOPIFY.COM" },
  { type: "spacer",    h: 64 },

  { type: "divider"        },
  { type: "spacer",    h: 36 },
  { type: "closing",   label: "© 2026 Plica Games  —  All Rights Reserved" },
  { type: "spacer",    h: 80 },

  { type: "divider"        },
  { type: "spacer",    h: 56 },

  // ── Teaser final ────────────────────────────────────────────────────────
  { type: "highlight", label: "NOXYS IS COMING" },
  { type: "spacer",    h: 18 },
  { type: "highlight", label: "13 / 8 / 2026" },
  { type: "spacer",    h: 16 },
  { type: "subtitle",  label: "SAVE THE DATE  •  SAVE PRISMARA" },
  { type: "spacer",    h: 200 }, // trailing — last line scrolls clear of logo
];

export default class CreditsScene extends Phaser.Scene {
  constructor() { super("CreditsScene"); }

  // ── preload ───────────────────────────────────────────────────────────────
  preload() {
    this.load.image("credits-logo", logoImg);
    this.load.image("credits-bg",   creditsBg);

    // 🔊 MUSIC — ecrã de créditos
    this.load.audio("bgm_credits", creditsMusicUrl);
  }

  // ── create ────────────────────────────────────────────────────────────────
  create() {
    const W = this.scale.width;   // 540
    const H = this.scale.height;  // 960

    // ── 1. BACKGROUND + DARK OVERLAY ─────────────────────────────────────────
    this.add.image(W / 2, H / 2, "credits-bg")
      .setDisplaySize(W, H)
      .setDepth(0);

    // Semi-transparent overlay so colourful art doesn't overpower the text
    const overlay = this.add.graphics().setDepth(1);
    overlay.fillStyle(0x000000, 0.74);
    overlay.fillRect(0, 0, W, H);

    // 🔊 Música dos créditos em loop
    this.bgm = this.sound.add("bgm_credits", { loop: true, volume: 0.50 });
    this.bgm.play();
    this.events.once("shutdown", () => this.bgm?.stop());

    // ── 2. BUILD SCROLLING CONTENT ────────────────────────────────────────────
    // All items are created at relative y positions (0 = top of roll).
    // They're collected into a Container that starts below the screen and
    // tweens up — classic cinematic credits roll.
    const CX    = W / 2;   // horizontal center (relative to container x=0)
    const TXT_W = 390;     // max line width before word-wrap

    let curY = 0;
    const items = [];

    // Helpers — objects added to scene first, then moved into container
    const mkText = (x, y, str, style) => {
      const t = this.add.text(x, y, str, { ...style }).setOrigin(0.5, 0);
      items.push(t);
      return t;
    };
    const mkGfx = (relX, relY) => {
      const g = this.add.graphics({ x: relX, y: relY });
      items.push(g);
      return g;
    };

    for (const entry of CREDITS) {
      switch (entry.type) {

        // ── spacer ──────────────────────────────────────────────────────────
        case "spacer":
          curY += entry.h;
          break;

        // ── section header ──────────────────────────────────────────────────
        case "section": {
          // Thin gold accent line above the label
          const g = mkGfx(0, curY);
          g.lineStyle(1, GOLD, 0.42);
          g.beginPath();
          g.moveTo(CX - 95, 0);
          g.lineTo(CX + 95, 0);
          g.strokePath();
          curY += 10;

          mkText(CX, curY, entry.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "15px",
            color: "#fbbf24",
            fontStyle: "bold",
            align: "center",
          });
          curY += 30;
          break;
        }

        // ── game subtitle ────────────────────────────────────────────────────
        case "subtitle":
          mkText(CX, curY, entry.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "12px",
            color: "#94a3b8",
            fontStyle: "italic",
            align: "center",
          });
          curY += 20;
          break;

        // ── credited person's name ───────────────────────────────────────────
        case "name":
          mkText(CX, curY, entry.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "17px",
            color: "#e2e8f0",
            fontStyle: "bold",
            align: "center",
          });
          curY += 26;
          break;

        // ── role / function label ────────────────────────────────────────────
        case "role":
          mkText(CX, curY, entry.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "12px",
            color: "#64748b",
            fontStyle: "italic",
            align: "center",
          });
          curY += 20;
          break;

        // ── multi-line body (wraps) ──────────────────────────────────────────
        case "body": {
          const t = mkText(CX, curY, entry.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "13px",
            color: "#94a3b8",
            align: "center",
            wordWrap: { width: TXT_W },
          });
          // t.height is available synchronously in Phaser 3
          curY += (t.height || 20) + 8;
          break;
        }

        // ── closing message ──────────────────────────────────────────────────
        case "closing":
          mkText(CX, curY, entry.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "14px",
            color: "#fde68a",
            fontStyle: "italic",
            align: "center",
          });
          curY += 24;
          break;

        // ── URL / link ───────────────────────────────────────────────────────
        case "link":
          mkText(CX, curY, entry.label, {
            fontFamily: "system-ui, Arial",
            fontSize: "11px",
            color: "#38bdf8",
            fontStyle: "italic",
            align: "center",
          });
          curY += 20;
          break;

        // ── dramatic highlight (NOXYS teaser) ────────────────────────────────
        case "highlight":
          mkText(CX, curY, entry.label, {
            fontFamily: "Kamikaze, system-ui, Arial",
            fontSize: "34px",
            color: "#ef4444",
            fontStyle: "bold",
            stroke: "#7f1d1d",
            strokeThickness: 4,
            align: "center",
          });
          curY += 50;
          break;

        // ── horizontal divider ───────────────────────────────────────────────
        case "divider": {
          const g = mkGfx(0, curY);
          g.lineStyle(1, GOLD, 0.26);
          g.beginPath();
          g.moveTo(CX - 120, 0);
          g.lineTo(CX + 120, 0);
          g.strokePath();
          curY += 4;
          break;
        }
      }
    }

    const totalH = curY; // total height of the credits roll content

    // ── Place all items into a Container that starts below the screen ─────────
    const roll = this.add.container(0, H, items).setDepth(2);

    // ── 3. SCROLL TWEEN ───────────────────────────────────────────────────────
    // Travel distance = H (starting offset) + totalH (content length)
    // At ~24 ms/px this feels like a proper cinematic credit roll (~35-45 s)
    const SCROLL_SPEED_MS_PER_PX = 24;
    const scrollDuration = Math.max(18000, (H + totalH) * SCROLL_SPEED_MS_PER_PX);

    // Short pause before rolling so the player can take in the background
    this.time.delayedCall(900, () => {
      this.tweens.add({
        targets:  roll,
        y:        -(totalH),       // scroll content completely off the top
        duration: scrollDuration,
        ease:     "Linear",
        onComplete: () => this._onRollComplete(),
      });
    });

    // ── 4. GRADIENT MASKS (hide text under fixed header / footer) ─────────────
    // Top fade — covers the logo band so text fades in smoothly
    const topFade = this.add.graphics().setDepth(4);
    topFade.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.95, 0.95, 0, 0);
    topFade.fillRect(0, 0, W, 142);

    // Bottom fade — covers the close-button band
    const botFade = this.add.graphics().setDepth(4);
    botFade.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.95, 0.95);
    botFade.fillRect(0, H - 130, W, 130);

    // ── 5. LOGO (fixed at top, depth above gradient) ──────────────────────────
    const logo = this.add
      .image(W / 2, 66, "credits-logo")
      .setDepth(5)
      .setAlpha(0);
    // Scale logo to 58% of canvas width (smaller than TitleScene — feels elegant)
    logo.setScale(Math.min(W * 0.58, W - 40) / logo.width);

    // Subtle gold line beneath logo
    const logoLine = this.add.graphics().setDepth(5);
    logoLine.lineStyle(1.5, GOLD, 0.36);
    logoLine.beginPath();
    logoLine.moveTo(W / 2 - 115, 106);
    logoLine.lineTo(W / 2 + 115, 106);
    logoLine.strokePath();
    logoLine.setAlpha(0);

    this.tweens.add({
      targets: [logo, logoLine],
      alpha: 1,
      duration: 700,
      delay: 200,
      ease: "Quad.Out",
    });

    // ── 6. CLOSE BUTTON (fixed at bottom, depth above all) ────────────────────
    const BTN_W  = 188;
    const BTN_H  = 50;
    const BTN_CY = H - 58;

    const closeBg = this.add
      .rectangle(W / 2, BTN_CY, BTN_W, BTN_H, 0x060d18, 0.92)
      .setStrokeStyle(2, GOLD, 0.68)
      .setDepth(6)
      .setAlpha(0);

    const closeTxt = this.add.text(W / 2, BTN_CY, I18n.t('credits_close'), {
      fontFamily: "system-ui, Arial",
      fontSize: "14px",
      color: "#fbbf24",
      fontStyle: "bold",
    }).setOrigin(0.5).setDepth(7).setAlpha(0);

    // Invisible hit-area on top
    const closeHit = this.add
      .rectangle(W / 2, BTN_CY, BTN_W, BTN_H, 0xffffff, 0)
      .setDepth(8)
      .setInteractive({ useHandCursor: true });

    closeHit.on("pointerover",  () => closeBg.setFillStyle(0x1a2c46, 0.96));
    closeHit.on("pointerout",   () => closeBg.setFillStyle(0x060d18, 0.92));
    closeHit.on("pointerdown",  () =>
      this.tweens.add({
        targets: [closeBg, closeTxt],
        scaleX: 0.94, scaleY: 0.94,
        duration: 70, yoyo: true,
      })
    );
    closeHit.on("pointerup", () => this._exitToTitle());

    this.tweens.add({
      targets: [closeBg, closeTxt],
      alpha: 1,
      duration: 500,
      delay: 500,
      ease: "Quad.Out",
    });
  }

  // ── _onRollComplete ───────────────────────────────────────────────────────
  // Called automatically when all credits have scrolled off the top.
  // Plays a brief cinematic white flash then returns to the title screen.
  _onRollComplete() {
    const W = this.scale.width;
    const H = this.scale.height;

    const flash = this.add
      .rectangle(W / 2, H / 2, W, H, 0xffffff, 0)
      .setDepth(20);

    this.tweens.add({
      targets:  flash,
      alpha:    0.85,
      duration: 900,
      ease:     "Quad.In",
      hold:     700,
      yoyo:     true,
      onComplete: () => this._exitToTitle(),
    });
  }

  // ── _exitToTitle ─────────────────────────────────────────────────────────
  // Central exit point used by both FECHAR button and auto-end.
  _exitToTitle() {
    // 🔊 Fade de saída antes de voltar ao título
    if (this.bgm?.isPlaying) {
      this.tweens.add({
        targets: this.bgm, volume: 0, duration: 600,
        onComplete: () => { this.bgm.stop(); this.scene.start("TitleScene"); },
      });
      return;
    }
    this.scene.start("TitleScene");
  }
}
